import { useEffect, useMemo, useState } from "react";
import isEmpty from "lodash/isEmpty";
import uniqBy from "lodash/uniqBy";
import isEqual from "lodash/isEqual";
import sortBy from "lodash/sortBy";

import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import { highlightText } from "@/helpers/highlight-text";

import { scrollIntoView } from "@/lib";

/** Module-level dedupe survives React Strict Mode remounts (refs do not). Exported for tests. */
export const recentFetchTimestamps = new Map<string, number>();
const FETCH_DEDUP_MS = 1500;

/** Evict entries past the dedupe window so the module-level Map can't grow unbounded. */
export const pruneStaleFetchTimestamps = (now: number) => {
  recentFetchTimestamps.forEach((ts, key) => {
    if (now - ts >= FETCH_DEDUP_MS) recentFetchTimestamps.delete(key);
  });
};

export default function useInfiniteScroll(
  apiFunction: any,
  apiParams: any,
  content: Array<object>,
  pageNumber: number,
  contentLimiter: number,
  onParamChangeCheck: object,
  contentCount: number,
  toolError?: string,
  highlightClass?: string
) {
  const dispatch = useAppDispatch();
  const { layout } = useAppSelector(
    (state) => state.brainstormers.brainstormer
  );

  // hasMore flag decides if we have any more net result
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [previousData, setPreviousData] = useState([]);
  const [updatedParam, setUpdatedParam] = useState({});
  const [hasPageLoadTriggered, setHasPageLoadTriggered] = useState(false);
  const [hasNewContentAdded, setHasNewContentAdded] = useState(false);

  const fetchKey = useMemo(
    () =>
      JSON.stringify({
        page: apiParams?.page,
        pageNumber,
        params: onParamChangeCheck,
        projectId: apiParams?.projectId,
        brainstormerId: apiParams?.brainstormerId,
        search: apiParams?.search,
        categoryId: apiParams?.categoryId
      }),
    [apiParams, pageNumber, onParamChangeCheck]
  );

  const fetchData = async () => {
    const now = Date.now();
    const last = recentFetchTimestamps.get(fetchKey);
    if (last != null && now - last < FETCH_DEDUP_MS) {
      return false;
    }
    // Stamp before dispatch so React's synchronous double-invoke (Strict Mode)
    // is deduped; prune stale keys first to cap the Map's growth.
    pruneStaleFetchTimestamps(now);
    recentFetchTimestamps.set(fetchKey, now);
    try {
      const result: any = await dispatch(apiFunction(apiParams));
      // A rejected fetch must not block a retry for the whole dedupe window.
      if (result?.meta?.requestStatus === "rejected") {
        recentFetchTimestamps.delete(fetchKey);
        return false;
      }
      return true;
    } catch (error) {
      recentFetchTimestamps.delete(fetchKey);
      throw error;
    }
  };

  useEffect(() => {
    setHasNewContentAdded(true);
    if (apiParams?.search) {
      void fetchData();
    }
    // Intentionally only when contentCount changes (new post / filter refresh).
  }, [contentCount]);

  const contentRemain = useMemo(() => {
    const { page } = apiParams;
    const prevPage = page === 1 ? 1 : page - 1;
    // checking if the total number of content and the number of contents loaded in the previous API pages
    const remaining = contentCount - prevPage * contentLimiter;
    return remaining > contentLimiter ? contentLimiter : remaining;
  }, [contentCount, apiParams, contentLimiter]);

  useEffect(() => {
    if (toolError) {
      setLoading(false);
      return;
    }

    const now = Date.now();
    const last = recentFetchTimestamps.get(fetchKey);
    if (last != null && now - last < FETCH_DEDUP_MS) {
      // Same request just fired (Strict Mode / hydration remount) — skip the
      // dispatch, but always drop the spinner so it can't stick on a remount
      // that lands before content arrives.
      setLoading(false);
      return;
    }

    setLoading(true);
    if (!isEqual(onParamChangeCheck, updatedParam)) setPreviousData([]);
    setHasPageLoadTriggered(true);
    void fetchData();
    // fetchKey already encodes pageNumber + onParamChangeCheck; depending on the
    // raw object too would refire on a same-content new identity.
  }, [toolError, fetchKey]);

  useEffect(() => {
    if (isEmpty(content)) {
      if (contentCount <= 0) setData([]);
      setHasMore(false);
      setLoading(false);
      return;
    }

    if (contentRemain < 0) return;
    setData(content);
    setHasMore(content.length >= contentLimiter);

    setData((prev: { [key: string]: string }[]) => {
      let fetchedContent = prev;
      if (
        previousData.length > 0 &&
        isEqual(onParamChangeCheck, updatedParam)
      ) {
        fetchedContent = previousData;
      }

      let cumulatedResult = [...fetchedContent, ...content];

      if (
        !hasPageLoadTriggered &&
        cumulatedResult.length > 2 * content.length
      ) {
        cumulatedResult = sortBy(cumulatedResult, "id").reverse();
      }
      if (hasNewContentAdded) {
        if (layout !== "BudgetAllocator") scrollIntoView("#ideas-grid");
      }

      const uniqResult = uniqBy(cumulatedResult, "id");

      setPreviousData([...uniqResult, ...previousData]);
      setUpdatedParam({ ...onParamChangeCheck });
      setLoading(false);
      setHasPageLoadTriggered(false);
      setHasNewContentAdded(false);

      return uniqResult;
    });

    if (highlightClass && apiParams.search) {
      setTimeout(() => {
        highlightText(highlightClass, apiParams.search);
      }, 100);
    }
  }, [content]);

  return { loading, data, hasMore, contentRemain };
}
